import { combineReducers } from 'redux';
import appConfig from '@nerdwallet/app-config';
import type { SegmentTrackingAttributes } from '../lib/helpers';
import type { ReduxAction } from './actions';
import { isSetNavStateAction } from './actions';
import {
  SHOW_REG_SUCCESS_STATUS,
  SHOW_REG_INBOX_SUCCESS_STATUS,
  SHOW_REG_ERROR_STATUS,
  HIDE_REG_STATUS,
} from './constants';
import type { ReduxStore } from './types';
import { initialStore } from './types';

const firstName = (state = '', action: ReduxAction) => {
  if (isSetNavStateAction(action) && action.firstName !== undefined) {
    return action.firstName;
  }

  return state;
};

const isLoggedIn = (state = false, action: ReduxAction) => {
  if (isSetNavStateAction(action) && action.isLoggedIn !== undefined) {
    return action.isLoggedIn;
  }

  return state;
};

const pageVertical = (state = '', action: ReduxAction) => {
  if (isSetNavStateAction(action) && action.pageVertical !== undefined) {
    return action.pageVertical;
  }

  return state;
};

const pageTaxonomy = (
  state: ReduxStore['pageTaxonomy'] = [],
  action: ReduxAction
) => {
  if (isSetNavStateAction(action) && action.pageTaxonomy !== undefined) {
    return action.pageTaxonomy;
  }

  return state;
};

const clientID = (state = '', action: ReduxAction) => {
  if (isSetNavStateAction(action) && action.clientID !== undefined) {
    return action.clientID;
  }

  return state;
};

const originatingUrl = (state = '', action: ReduxAction) => {
  if (isSetNavStateAction(action) && action.originatingUrl !== undefined) {
    return action.originatingUrl;
  }

  return state;
};

const windowSize = (state = { width: 0, height: 0 }, action: ReduxAction) => {
  if (action.type === 'WINDOW_RESIZE') {
    return { width: action.width, height: action.height };
  }

  return state;
};

const defaultSavedArticlesMenuItem: ReduxStore['savedArticlesMenuItem'] = {
  name: '',
  value: false,
  extraData: {
    title: '',
    uri: '',
  },
};

const savedArticlesMenuItem = (
  state: ReduxStore['savedArticlesMenuItem'] = defaultSavedArticlesMenuItem,
  action: ReduxAction
) => {
  if (
    isSetNavStateAction(action) &&
    action.savedArticlesMenuItem !== undefined
  ) {
    return action.savedArticlesMenuItem;
  }

  return state;
};

const areWebPushNotificationsActive = (state = false, action: ReduxAction) => {
  if (
    isSetNavStateAction(action) &&
    action.areWebPushNotificationsActive !== undefined
  ) {
    return action.areWebPushNotificationsActive;
  }

  return state;
};

const nerdWalletPlusActive = (
  state: ReduxStore['nerdWalletPlusActive'] = false,
  action: ReduxAction
) => {
  if (
    isSetNavStateAction(action) &&
    action.nerdWalletPlusActive !== undefined
  ) {
    return action.nerdWalletPlusActive;
  }

  return state;
};

const nerdWalletPlusFeatureDiscovery = (
  state: ReduxStore['nerdWalletPlusFeatureDiscovery'] = false,
  action: ReduxAction
) => {
  if (
    isSetNavStateAction(action) &&
    action.nerdWalletPlusFeatureDiscovery !== undefined
  ) {
    return action.nerdWalletPlusFeatureDiscovery;
  }

  return state;
};

const creditBuilderLoanActive = (
  state: ReduxStore['creditBuilderLoanActive'] = false,
  action: ReduxAction
) => {
  if (
    isSetNavStateAction(action) &&
    action.creditBuilderLoanActive !== undefined
  ) {
    return action.creditBuilderLoanActive;
  }

  return state;
};

const activeRegStatusNotification = (
  state = { status: HIDE_REG_STATUS },
  action: ReduxAction
) => {
  switch (action.type) {
    case SHOW_REG_SUCCESS_STATUS:
      return { status: SHOW_REG_SUCCESS_STATUS };
    case SHOW_REG_ERROR_STATUS:
      return {
        status: SHOW_REG_ERROR_STATUS,
        reason: action.reason,
        analytics: action.analytics,
      };
    case HIDE_REG_STATUS:
      return { status: HIDE_REG_STATUS };
    case SHOW_REG_INBOX_SUCCESS_STATUS:
      return { status: SHOW_REG_INBOX_SUCCESS_STATUS };
    default:
      return state;
  }
};

const abTests = (state: ReduxStore['abTests'] = [], action: ReduxAction) => {
  if (isSetNavStateAction(action) && action.abTests !== undefined) {
    return action.abTests;
  }

  return state;
};

const experiments = (
  state: ReduxStore['experiments'] = [],
  action: ReduxAction
) => {
  if (isSetNavStateAction(action) && action.experiments !== undefined) {
    return action.experiments;
  }

  return state;
};

const segmentTrackingAttributes = (
  state: SegmentTrackingAttributes = {
    driverPageName: '',
    // eslint-disable-next-line no-underscore-dangle
    deployableName: appConfig?._meta?.deployable,
    // eslint-disable-next-line no-underscore-dangle
    deployableVersion: appConfig?._meta?.version,
    driverPageViewId: '',
    driverContentId: '',
    driverContentSource: '',
    driverFlowName: '',
  },
  action: ReduxAction
) => {
  if (
    isSetNavStateAction(action) &&
    action.segmentTrackingAttributes !== undefined
  ) {
    return {
      ...state,
      ...action.segmentTrackingAttributes,
    };
  }

  return state;
};

const authParamsOverride = (
  state: ReduxStore['authParamsOverride'] = null,
  action: ReduxAction
) => {
  if (isSetNavStateAction(action) && action.authParamsOverride !== undefined) {
    return {
      ...state,
      ...action.authParamsOverride,
    };
  }

  return state;
};

const combinedReducers = combineReducers<ReduxStore>({
  firstName,
  isLoggedIn,
  pageVertical,
  clientID,
  originatingUrl,
  pageTaxonomy,
  windowSize,
  activeRegStatusNotification,
  savedArticlesMenuItem,
  areWebPushNotificationsActive,
  nerdWalletPlusActive,
  creditBuilderLoanActive,
  abTests,
  experiments,
  segmentTrackingAttributes,
  authParamsOverride,
  nerdWalletPlusFeatureDiscovery,
});

export default (state = initialStore, action: ReduxAction) =>
  combinedReducers(state, action);
